import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, withText } from 'preact-i18n';

import localUtil from '../../lib/util';
import OrganizersPasteImport from './organizersPasteImport';

const FUNCTION_SUGGESTIONS = [
    'Lag 1', 'Lag 2', 'Lag 3', 'Lag 4', 'Lag 5',
    'Lag 6', 'Lag 7', 'Lag 8', 'Lag 9', 'Lag 10',
    'Påmeldingskiosk', 'Kafeteria', 'Tidtaking',
    'Standplassansvarlig', 'Sekretariat',
];

@observer
class ConventionViewOrganizersAdmin extends Component {
    constructor(props) {
        super(props);
        this.state = {
            search: '',
            assignedFunction: '',
            editingMd5: null,
            editingFunction: '',
            // SMS recipient selection. Tracked as EXCLUSIONS (md5 -> true) so the
            // default (empty) means "all selected", and organizers added later are
            // included automatically without re-seeding state.
            excluded: {},
            // View-only sorting of the organizer table. Empty sortBy = stored order.
            sortBy: '',
            sortDirection: 'asc',
            savingOrder: false,
            draggingMd5: null,
            dragOver: null,
        };
    }

    searchInput = (e) => {
        const search = e.target.value;
        this.setState({ search });

        clearTimeout(this.searchTimer);
        if (search.length >= 3) {
            this.searchTimer = setTimeout(() => {
                this.doSearch(search);
            }, 300);
        }
    }

    async doSearch(search) {
        const { userStore } = this.props.stores;
        if (search) {
            await userStore.searchUsersAll({ search, showAll: 1 });
        } else {
            userStore.localUpdateField('searchUsersResult', []);
        }
    }

    clearSearch = () => {
        this.setState({ search: '' });
        this.doSearch('');
    }

    handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.doSearch(this.state.search);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.clearSearch();
        }
    }

    addOrganizer = async (e) => {
        const { id } = e.target.closest('button').dataset;
        const { loadAll = () => {} } = this.props;
        const { conventionStore, userStore } = this.props.stores;
        const { searchUsersResult } = userStore;
        const user = searchUsersResult.find(u => u.id === parseInt(id, 10));
        if (!user) {
            return;
        }
        const { assignedFunction } = this.state;
        const result = await conventionStore.addOrganizer(user, assignedFunction);
        if (result && result.status === 409) {
            const { appState } = this.props.stores;
            appState.addToast({ type: 'warning', message: 'Personen er allerede lagt til som arrangør' }, 3000);
            return;
        }
        this.setState({ search: '', assignedFunction: '' });
        this.doSearch('');
        await loadAll();
    }

    removeOrganizer = async (e) => {
        const { md5 } = e.target.closest('button').dataset;
        const { loadAll = () => {} } = this.props;
        const { conventionStore } = this.props.stores;
        await conventionStore.removeOrganizer(md5);
        await loadAll();
    }

    startEditFunction = (e) => {
        const { md5 } = e.target.closest('button').dataset;
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const organizers = convention.organizers || [];
        const organizer = organizers.find(o => o.md5 === md5);
        this.setState({
            editingMd5: md5,
            editingFunction: organizer ? organizer.function || '' : '',
        });
    }

    handleEditFunctionInput = (e) => {
        this.setState({ editingFunction: e.target.value });
    }

    saveEditFunction = async () => {
        const { editingMd5, editingFunction } = this.state;
        if (!editingMd5) {
            return;
        }
        const { loadAll = () => {} } = this.props;
        const { conventionStore } = this.props.stores;
        await conventionStore.editOrganizerFunction(editingMd5, editingFunction);
        this.setState({ editingMd5: null, editingFunction: '' });
        await loadAll();
    }

    handleEditKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.saveEditFunction();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.setState({ editingMd5: null, editingFunction: '' });
        }
    }

    // Click a column header to sort the list. Clicking the active column flips
    // the direction. This only changes the view - the stored order is untouched.
    toggleSort = (e) => {
        const { field } = e.target.closest('th').dataset;
        const { sortBy, sortDirection, savingOrder } = this.state;
        if (savingOrder) {
            return;
        }
        this.handleDragEnd();
        if (sortBy === field) {
            this.setState({ sortDirection: sortDirection === 'asc' ? 'desc' : 'asc' });
            return;
        }
        this.setState({ sortBy: field, sortDirection: 'asc' });
    }

    clearSort = () => {
        if (this.state.savingOrder) {
            return;
        }
        this.setState({ sortBy: '', sortDirection: 'asc', draggingMd5: null, dragOver: null });
    }

    sortOrganizers = (a, b) => {
        const { sortBy, sortDirection } = this.state;
        // numeric: true so 'Stp 2' sorts before 'Stp 10', not after.
        const opts = { sensitivity: 'base', numeric: true };
        let result = 0;
        if (sortBy === 'name') {
            const aName = `${a.firstname || ''} ${a.lastname || ''}`.trim();
            const bName = `${b.firstname || ''} ${b.lastname || ''}`.trim();
            result = aName.localeCompare(bName, 'nb', opts);
        } else if (sortBy === 'cellphone') {
            result = (a.cellphone || '').localeCompare(b.cellphone || '', 'nb', opts);
        } else if (sortBy === 'function') {
            result = (a.function || '').localeCompare(b.function || '', 'nb', opts);
        }
        if (result !== 0) {
            return sortDirection === 'asc' ? result : -result;
        }
        // Ties (and the '#' column) fall back to the stored order.
        const indexDiff = a.sortIndex - b.sortIndex;
        if (sortBy === 'index') {
            return sortDirection === 'asc' ? indexDiff : -indexDiff;
        }
        return indexDiff;
    }

    // The list as currently displayed. Always a copy of the store's array with a
    // sortIndex marker - the observable array itself is never sorted in place.
    getSortedOrganizers = () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const { sortBy } = this.state;
        const indexed = (convention.organizers || []).map((o, idx) => ({ ...o, sortIndex: idx }));
        if (!sortBy) {
            return indexed;
        }
        return indexed.sort(this.sortOrganizers);
    }

    // Write the current sort order back to the convention, so the list stays in
    // this order for everyone from now on.
    saveSortedOrder = async () => {
        const { loadAll = () => {} } = this.props;
        const { appState, conventionStore } = this.props.stores;
        const { sortBy, savingOrder } = this.state;
        if (!sortBy || savingOrder) {
            return;
        }
        const sorted = this.getSortedOrganizers().map((o) => {
            const organizer = { ...o };
            delete organizer.sortIndex;
            return organizer;
        });
        this.setState({ savingOrder: true });
        const response = await conventionStore.saveOrganizerOrder(sorted);
        const savedOk = response && response.status >= 200 && response.status < 300;
        if (!savedOk) {
            this.setState({ savingOrder: false });
            appState.addToast({ type: 'danger', message: 'Klarte ikke å lagre rekkefølgen' }, 3000);
            return;
        }
        await loadAll();
        // The stored order now matches what is shown, so drop the view sort.
        this.setState({ sortBy: '', sortDirection: 'asc', savingOrder: false });
        appState.addToast({ type: 'success', message: 'Rekkefølgen er lagret' }, 3000);
    }

    // Move an organizer one step up or down in the STORED order and persist
    // right away. Only offered when no column sort is active, since moving a
    // row only makes sense against the order that is actually saved.
    moveOrganizer = async (e) => {
        const { md5, direction } = e.target.closest('button').dataset;
        const { conventionStore } = this.props.stores;
        const organizers = conventionStore.convention.organizers || [];
        const index = organizers.findIndex(o => o.md5 === md5);
        await this.moveOrganizerToIndex(md5, direction === 'up' ? index - 1 : index + 1);
    }

    // Both arrows and dragging operate on a copy of the saved list. Reload only
    // after a successful save, so a failed request leaves the displayed order intact.
    moveOrganizerToIndex = async (md5, targetIndex) => {
        const { sortBy, savingOrder, editingMd5 } = this.state;
        if (sortBy || savingOrder || editingMd5) {
            return;
        }
        const { loadAll = () => {} } = this.props;
        const { appState, conventionStore } = this.props.stores;
        const organizers = conventionStore.convention.organizers || [];
        const index = organizers.findIndex(o => o.md5 === md5);
        if (index < 0 || targetIndex < 0 || targetIndex >= organizers.length || index === targetIndex) {
            return;
        }
        const reordered = organizers.map(o => ({ ...o }));
        const [organizer] = reordered.splice(index, 1);
        reordered.splice(targetIndex, 0, organizer);
        this.setState({ savingOrder: true });
        let savedOk = false;
        try {
            const response = await conventionStore.saveOrganizerOrder(reordered);
            savedOk = response && response.status >= 200 && response.status < 300;
            if (!savedOk) {
                appState.addToast({ type: 'danger', message: 'Klarte ikke å lagre rekkefølgen' }, 3000);
                return;
            }
            await loadAll();
        } catch (err) {
            const message = savedOk
                ? 'Rekkefølgen er lagret, men listen kunne ikke oppdateres. Åpne listen på nytt.'
                : 'Klarte ikke å lagre rekkefølgen';
            appState.addToast({ type: 'danger', message }, 3000);
        } finally {
            this.setState({ savingOrder: false });
        }
    }

    handleDragStart = (e) => {
        const { sortBy, savingOrder, editingMd5 } = this.state;
        const { md5 } = e.currentTarget.dataset;
        if (sortBy || savingOrder || editingMd5 || !md5) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', md5);
        const row = e.currentTarget.closest('tr');
        if (row && e.dataTransfer.setDragImage) {
            const rect = row.getBoundingClientRect();
            e.dataTransfer.setDragImage(row, e.clientX - rect.left, e.clientY - rect.top);
        }
        this.setState({ draggingMd5: md5, dragOver: null });
    }

    handleDragOver = (e) => {
        const { draggingMd5, sortBy, savingOrder, editingMd5, dragOver } = this.state;
        if (!draggingMd5 || sortBy || savingOrder || editingMd5) {
            return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const { md5 } = e.currentTarget.dataset;
        if (md5 === draggingMd5) {
            if (dragOver) this.setState({ dragOver: null });
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const after = e.clientY > rect.top + rect.height / 2;
        if (!dragOver || dragOver.md5 !== md5 || dragOver.after !== after) {
            this.setState({ dragOver: { md5, after } });
        }
    }

    handleDrop = async (e) => {
        const { draggingMd5, sortBy, savingOrder, editingMd5 } = this.state;
        this.handleDragEnd();
        if (!draggingMd5 || sortBy || savingOrder || editingMd5) {
            return;
        }
        e.preventDefault();
        const { md5 } = e.currentTarget.dataset;
        const { conventionStore } = this.props.stores;
        const organizers = conventionStore.convention.organizers || [];
        const fromIndex = organizers.findIndex(o => o.md5 === draggingMd5);
        const toIndex = organizers.findIndex(o => o.md5 === md5);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const after = e.clientY > rect.top + rect.height / 2;
        const insertionIndex = toIndex + (after ? 1 : 0);
        const targetIndex = insertionIndex - (fromIndex < insertionIndex ? 1 : 0);
        await this.moveOrganizerToIndex(draggingMd5, targetIndex);
    }

    handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            this.setState({ dragOver: null });
        }
    }

    handleDragEnd = () => {
        this.setState({ draggingMd5: null, dragOver: null });
    }

    // Toggle a single organizer in/out of the SMS recipient selection.
    toggleSelectOrganizer = (e) => {
        const { md5 } = e.target.dataset;
        this.setState((prev) => {
            const excluded = { ...prev.excluded };
            if (excluded[md5]) {
                delete excluded[md5];
            } else {
                excluded[md5] = true;
            }
            return { excluded };
        });
    }

    // Header checkbox: select all when not everyone is selected, else deselect all.
    // Only organizers with a phone can be recipients.
    toggleSelectAll = () => {
        const { conventionStore } = this.props.stores;
        const withPhone = (conventionStore.convention.organizers || []).filter(o => o.cellphone);
        const { excluded } = this.state;
        const allSelected = withPhone.length > 0 && withPhone.every(o => !excluded[o.md5]);
        if (allSelected) {
            const newExcluded = {};
            withPhone.forEach(o => { newExcluded[o.md5] = true; });
            this.setState({ excluded: newExcluded });
        } else {
            this.setState({ excluded: {} });
        }
    }

    sendSmsToOrganizers = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const organizers = convention.organizers || [];
        const { excluded } = this.state;
        const { drawerHeightMediumLarge } = appState;

        // Only the checked organizers that have a phone number.
        const users = organizers
            .filter(o => o.cellphone && !excluded[o.md5])
            .map(o => ({
                firstname: o.firstname,
                lastname: o.lastname,
                cellphone: o.cellphone,
            }));

        if (users.length === 0) {
            return;
        }

        appState.openDrawer('sendSms', {
            height: drawerHeightMediumLarge,
            users,
            from: convention.title || 'Stevne',
            // Let the admin set a provider-valid SMS sender (a-zA-Z0-9, max 11 chars);
            // the convention title is only a seed and is sanitized by the drawer.
            editableSender: true,
        }, drawerLevel + 1);
    }

    // Personalized SMS with each organizer's funksjon at this convention. The
    // sendSms drawer fills the {{flettefelt}} per recipient from mergeFields.
    sendFunctionSmsToOrganizers = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const organizers = convention.organizers || [];
        const { excluded } = this.state;
        const { drawerHeightMediumLarge } = appState;

        const users = organizers
            .filter(o => o.cellphone && !excluded[o.md5])
            .map(o => ({
                firstname: o.firstname,
                lastname: o.lastname,
                cellphone: o.cellphone,
                mergeFields: {
                    fornavn: o.firstname || '',
                    etternavn: o.lastname || '',
                    funksjon: o.function || '',
                    stevne: convention.title || '',
                },
            }));

        if (users.length === 0) {
            return;
        }

        appState.openDrawer('sendSms', {
            height: drawerHeightMediumLarge,
            users,
            personalized: true,
            message: 'Hei {{fornavn}}! Du er satt opp som arrangør på {{stevne}}. Din funksjon: {{funksjon}}. Ta kontakt om noe ikke stemmer.',
            from: convention.title || 'Stevne',
            editableSender: true,
        }, drawerLevel + 1);
    }

    render() {
        const { searchTitle } = this.props;
        const {
            search, assignedFunction, editingMd5, editingFunction, excluded, sortBy, sortDirection, savingOrder,
            draggingMd5, dragOver,
        } = this.state;
        const { userStore, conventionStore } = this.props.stores;
        const { user = {}, searchUsersResult } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const { convention } = conventionStore;
        const organizers = convention.organizers || [];
        const sortedOrganizers = this.getSortedOrganizers();
        const reorderDisabled = savingOrder || !!editingMd5;
        const dropBorderClass = [
            'border-primary border-2 border-start-0 border-end-0',
            dragOver?.after ? 'border-bottom border-top-0' : 'border-top border-bottom-0',
        ].join(' ');
        // Only offer to persist when sorting actually moved someone.
        const orderChanged = !!sortBy
            && organizers.map(o => o.md5).join('|') !== sortedOrganizers.map(o => o.md5).join('|');

        // SMS recipient selection (only organizers with a phone can receive SMS).
        const organizersWithPhone = organizers.filter(o => o.cellphone);
        const selectedCount = organizersWithPhone.filter(o => !excluded[o.md5]).length;
        const allSelected = organizersWithPhone.length > 0 && selectedCount === organizersWithPhone.length;
        const someSelected = selectedCount > 0 && !allSelected;

        return (<>
            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-3 text-danger'>
                Arrangører ({organizers.length})
            </div>
            <div class={`d-flex flex-column mt-0 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} overflow-auto border border-danger shadow`}>

                <div class='d-flex font-weight-lighter border-bottom mb-2'>
                    Legg til arrangør:
                </div>

                <div class='d-flex flex-column flex-md-row mb-2'>
                    <div class='flex-fill input-group me-md-2 mb-2 mb-md-0'>
                        <div class='input-group-text rounded-pill-left'>
                            <i class='fa-regular fa-magnifying-glass' />
                        </div>
                        <input
                            class={`form-control ${!search ? 'rounded-pill-right' : ''}`}
                            type='text'
                            value={search}
                            placeholder={`${searchTitle}`}
                            onInput={this.searchInput}
                            onKeyDown={this.handleSearchKeyDown}
                        />
                        {search && <>
                            <button class='btn btn-danger btn-sm rounded-pill-right' type='button' onClick={this.clearSearch}>
                                <i class='fa-solid fa-circle-xmark' />
                            </button>
                        </>}
                    </div>
                    <div class='input-group' style='max-width: 250px;'>
                        <input
                            class='form-control rounded-pill'
                            type='text'
                            list='organizer-functions'
                            value={assignedFunction}
                            placeholder='Funksjon...'
                            onInput={(e) => this.setState({ assignedFunction: e.target.value })}
                        />
                        <datalist id='organizer-functions'>
                            {FUNCTION_SUGGESTIONS.map(f => (
                                <option key={f} value={f} />
                            ))}
                        </datalist>
                    </div>
                </div>

                {searchUsersResult && searchUsersResult.length > 0 && <>
                    <div class='d-flex flex-wrap mb-3'>
                        {searchUsersResult.map(u => (
                            <button
                                key={u.id}
                                class='btn btn-sm btn-outline-primary rounded-pill mx-1 my-1'
                                onClick={this.addOrganizer}
                                data-id={u.id}
                            >
                                <i class='fa-duotone fa-plus' /> {localUtil.displayName(u)} {u.cellphone}
                            </button>
                        ))}
                    </div>
                </>}

                <div class='d-flex font-weight-lighter border-bottom mb-2 mt-2'>
                    Eller lim inn en liste:
                </div>
                <OrganizersPasteImport
                    stores={this.props.stores}
                    onImported={this.props.loadAll}
                />

                {organizers.length > 0 && <>
                    <div class='d-flex flex-row justify-content-between align-items-center font-weight-lighter border-bottom mb-2 mt-2'>
                        <div>Arrangør-liste:</div>
                        <div class='d-flex flex-wrap gap-2 align-items-center'>
                            {savingOrder && <small class='text-body-secondary' role='status'>
                                <i class='fa-duotone fa-spinner fa-spin me-2' />
                                Lagrer...
                            </small>}
                            {!!sortBy && <button
                                type='button'
                                class='btn btn-sm btn-outline-secondary rounded-pill mb-1'
                                onClick={this.clearSort}
                                disabled={savingOrder}
                            >
                                Vis lagret rekkefølge
                            </button>}
                            {orderChanged && <button
                                class='btn btn-sm btn-outline-primary rounded-pill mb-1'
                                onClick={this.saveSortedOrder}
                                disabled={savingOrder}
                            >
                                <i class='fa-duotone fa-arrow-down-short-wide me-2' />
                                Lagre denne rekkefølgen
                            </button>}
                        </div>
                    </div>
                    <div class='table-responsive'>
                        <table class='table table-sm table-striped table-hover'>
                            <thead>
                                <tr>
                                    <th scope='col'>
                                        <input
                                            type='checkbox'
                                            class='form-check-input'
                                            checked={allSelected}
                                            ref={el => { if (el) el.indeterminate = someSelected; }}
                                            onChange={this.toggleSelectAll}
                                            title='Velg alle / ingen'
                                        />
                                    </th>
                                    <th scope='col' onClick={this.toggleSort} data-field='index' style='cursor: pointer;'>
                                        <nobr>
                                            #
                                            <i class={`fa-duotone ${sortBy === 'index' ? (sortDirection === 'asc' ? 'fa-sort-down' : 'fa-sort-up') : 'fa-sort'} ms-2 ${sortBy !== 'index' ? 'opacity-25' : ''}`} />
                                        </nobr>
                                    </th>
                                    <th scope='col' onClick={this.toggleSort} data-field='name' style='cursor: pointer;'>
                                        <nobr>
                                            Navn
                                            <i class={`fa-duotone ${sortBy === 'name' ? (sortDirection === 'asc' ? 'fa-sort-down' : 'fa-sort-up') : 'fa-sort'} ms-2 ${sortBy !== 'name' ? 'opacity-25' : ''}`} />
                                        </nobr>
                                    </th>
                                    <th scope='col' onClick={this.toggleSort} data-field='cellphone' style='cursor: pointer;'>
                                        <nobr>
                                            Telefon
                                            <i class={`fa-duotone ${sortBy === 'cellphone' ? (sortDirection === 'asc' ? 'fa-sort-down' : 'fa-sort-up') : 'fa-sort'} ms-2 ${sortBy !== 'cellphone' ? 'opacity-25' : ''}`} />
                                        </nobr>
                                    </th>
                                    <th scope='col' onClick={this.toggleSort} data-field='function' style='cursor: pointer;'>
                                        <nobr>
                                            Funksjon
                                            <i class={`fa-duotone ${sortBy === 'function' ? (sortDirection === 'asc' ? 'fa-sort-down' : 'fa-sort-up') : 'fa-sort'} ms-2 ${sortBy !== 'function' ? 'opacity-25' : ''}`} />
                                        </nobr>
                                    </th>
                                    <th scope='col'>&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody onDragLeave={this.handleDragLeave}>
                                {sortedOrganizers.map((org, index) => (
                                    <tr
                                        key={org.md5}
                                        data-md5={org.md5}
                                        class={[
                                            draggingMd5 === org.md5 ? 'opacity-50' : '',
                                            dragOver?.md5 === org.md5 ? dropBorderClass : '',
                                        ].filter(Boolean).join(' ')}
                                        onDragOver={this.handleDragOver}
                                        onDrop={this.handleDrop}
                                    >
                                        <td>
                                            <input
                                                type='checkbox'
                                                class='form-check-input'
                                                checked={!!org.cellphone && !excluded[org.md5]}
                                                disabled={!org.cellphone}
                                                data-md5={org.md5}
                                                onChange={this.toggleSelectOrganizer}
                                                title={org.cellphone ? '' : 'Mangler telefon'}
                                            />
                                        </td>
                                        <td class='text-nowrap'>
                                            {!sortBy && <button
                                                type='button'
                                                class='btn btn-sm btn-link text-body-secondary p-0 me-2'
                                                style='cursor: grab;'
                                                tabIndex={-1}
                                                title='Dra for å endre rekkefølge'
                                                aria-label='Dra for å endre rekkefølge'
                                                draggable={!reorderDisabled}
                                                disabled={reorderDisabled}
                                                data-md5={org.md5}
                                                onDragStart={this.handleDragStart}
                                                onDragEnd={this.handleDragEnd}
                                            >
                                                <i class='fa-solid fa-grip-vertical' />
                                            </button>}
                                            {index + 1}
                                        </td>
                                        <td>
                                            <nobr>
                                                {org.firstname} {org.lastname}
                                                {org.sportsClub && <small class='text-body-secondary ms-2'>{org.sportsClub}</small>}
                                            </nobr>
                                        </td>
                                        <td><nobr>{org.cellphone}</nobr></td>
                                        <td>
                                            {editingMd5 === org.md5 ? <>
                                                <input
                                                    class='form-control form-control-sm'
                                                    type='text'
                                                    list='organizer-functions-edit'
                                                    value={editingFunction}
                                                    onInput={this.handleEditFunctionInput}
                                                    onKeyDown={this.handleEditKeyDown}
                                                    onBlur={this.saveEditFunction}
                                                    autoFocus
                                                />
                                                <datalist id='organizer-functions-edit'>
                                                    {FUNCTION_SUGGESTIONS.map(f => (
                                                        <option key={f} value={f} />
                                                    ))}
                                                </datalist>
                                            </> : <>
                                                <span>{org.function || <span class='text-muted'>-</span>}</span>
                                            </>}
                                        </td>
                                        <td>
                                            <div class='d-flex'>
                                                {!sortBy && <>
                                                    <button
                                                        class='btn btn-sm btn-outline-secondary rounded-pill me-1'
                                                        onClick={this.moveOrganizer}
                                                        data-md5={org.md5}
                                                        data-direction='up'
                                                        disabled={reorderDisabled || index === 0}
                                                        title='Flytt opp'
                                                    >
                                                        <i class='fa-duotone fa-arrow-up' />
                                                    </button>
                                                    <button
                                                        class='btn btn-sm btn-outline-secondary rounded-pill me-1'
                                                        onClick={this.moveOrganizer}
                                                        data-md5={org.md5}
                                                        data-direction='down'
                                                        disabled={
                                                            reorderDisabled || index === sortedOrganizers.length - 1
                                                        }
                                                        title='Flytt ned'
                                                    >
                                                        <i class='fa-duotone fa-arrow-down' />
                                                    </button>
                                                </>}
                                                {editingMd5 !== org.md5 && <>
                                                    <button
                                                        class='btn btn-sm btn-outline-secondary rounded-pill me-1'
                                                        onClick={this.startEditFunction}
                                                        data-md5={org.md5}
                                                        title='Endre funksjon'
                                                        disabled={savingOrder}
                                                    >
                                                        <i class='fa-duotone fa-pen-to-square' />
                                                    </button>
                                                </>}
                                                <button
                                                    class='btn btn-sm btn-outline-danger rounded-pill'
                                                    onClick={this.removeOrganizer}
                                                    data-md5={org.md5}
                                                    title='Fjern arrangør'
                                                    disabled={savingOrder}
                                                >
                                                    <i class='fa-duotone fa-trash' />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div class='d-flex flex-wrap justify-content-center mt-2'>
                        <button
                            class='btn btn-outline-success rounded-pill m-1'
                            onClick={this.sendSmsToOrganizers}
                            disabled={selectedCount === 0}
                        >
                            <i class='fa-duotone fa-message-sms me-2' />
                            Send SMS til valgte ({selectedCount})
                        </button>
                        <button
                            class='btn btn-outline-success rounded-pill m-1'
                            onClick={this.sendFunctionSmsToOrganizers}
                            disabled={selectedCount === 0}
                        >
                            <i class='fa-duotone fa-clipboard-user me-2' />
                            Send funksjons-SMS ({selectedCount})
                        </button>
                    </div>
                </>}

                {organizers.length === 0 && <>
                    <div class='text-muted text-center py-3'>
                        Ingen arrangører lagt til ennå. Bruk søkefeltet over for å legge til.
                    </div>
                </>}
            </div>
        </>);
    }
}

export { ConventionViewOrganizersAdmin };

export default withText(() => ({
    searchTitle: <Text id='input.search-for-user'>Search for user</Text>,
}))(ConventionViewOrganizersAdmin);
